import { useState, useEffect, useMemo } from 'react';
import { getProjectsByOwner, getOtherProjects, createProject, getProjectByFmxUrl, getAllProfiles, getCurrentProfile, getImportSummaryForProjects, updateProjectModules, claimProject, saveProjectCredentials } from '../db';
import { testFmxConnection, fetchFmxModules } from '../fmxSync';
import { downloadCSV, importRowsCount } from '../utils';
import { getImportOrder } from '../schemas';
import { supabase } from '../supabase';
import { T } from '../theme';
import { AppShell, TopBar, ContentHeader, Button, Badge, Meter, FilterChip } from './fmxkit';
import MigSidebar from './MigSidebar';
import UserMenu from './UserMenu';
import ProfileEditModal from './ProfileEditModal';
import AdminScreen from './AdminScreen';
import DashboardScreen from './DashboardScreen';
import ActivityScreen from './ActivityScreen';

function daysSince(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Module-aware progress: which of the project's applicable entity types are imported.
function computeProgress(project, importedSchemas) {
  const settings = project.card_settings || {};
  const fullOrder = getImportOrder(project.fmx_modules);
  const activeSchemas = fullOrder.filter(s => !settings[s]?.hidden);
  const completed = activeSchemas.filter(s => importedSchemas.has(s));
  return { total: activeSchemas.length, completed: completed.length };
}

const STATUS_META = {
  active:   { color: 'blueDark', label: 'In progress' },
  complete: { color: 'green',    label: 'Complete', icon: 'circle-check' },
  setup:    { color: 'gray',     label: 'Setup' },
};
function projectStatus(progress) {
  if (progress.total > 0 && progress.completed >= progress.total) return 'complete';
  if (progress.completed > 0) return 'active';
  return 'setup';
}

function OwnerChip({ name }) {
  const initials = (name && name !== 'Unassigned')
    ? name.split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '—';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <span style={{ width: 22, height: 22, borderRadius: T.radiusPill, background: T.blue200, color: T.brandActive, font: `700 10px/22px ${T.fontSans}`, textAlign: 'center', flexShrink: 0 }}>{initials}</span>
      {name}
    </span>
  );
}

export default function ProjectScreen({ user, onSelectProject, activeProjectId, activeWizardSchema, initialAdminTab = null, fieldOverrides, onFieldOverridesChanged }) {
  const [myProjects, setMyProjects] = useState([]);
  const [otherProjects, setOtherProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allProfiles, setAllProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [importSummaryMap, setImportSummaryMap] = useState({}); // { [pid]: { schemas:Set, rows:number, last:string } }
  const [importSummaryRows, setImportSummaryRows] = useState([]); // raw project_imports rows (Dashboard/Activity feeds)

  // Cross-project section (sidebar). Dashboard/Activity are full screens in a later step.
  const [section, setSection] = useState(initialAdminTab ? 'admin' : 'projects'); // 'projects' | 'dashboard' | 'activity' | 'admin'
  const [adminTab, setAdminTab] = useState(initialAdminTab || 'users');

  // Toolbar filters
  const [ownerFilter, setOwnerFilter] = useState('me');   // 'me' | 'anyone'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'complete' | 'setup'
  const [search, setSearch] = useState('');

  // Create form
  const [mode, setMode] = useState('idle'); // 'idle' | 'create'
  const [fmxSiteUrl, setFmxSiteUrl] = useState('');
  const [fmxApiEmail, setFmxApiEmail] = useState('');
  const [fmxApiPassword, setFmxApiPassword] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createStep, setCreateStep] = useState('');
  const [orgName, setOrgName] = useState('');
  const [manualName, setManualName] = useState('');
  const [needsManualName, setNeedsManualName] = useState(false);
  const [duplicateProject, setDuplicateProject] = useState(null);

  // User menu modals
  const [showProfileModal, setShowProfileModal] = useState(false);

  const profileMap = useMemo(() => {
    const map = {};
    for (const p of allProfiles) map[p.id] = p.full_name || p.email || 'Unknown';
    return map;
  }, [allProfiles]);

  const projectNameById = useMemo(() => {
    const map = {};
    for (const p of [...myProjects, ...otherProjects]) map[p.id] = p.name;
    return map;
  }, [myProjects, otherProjects]);

  const isAdmin = currentProfile?.role === 'admin';

  const loadProjects = async () => {
    setLoading(true);
    const [mine, others, profiles, profile] = await Promise.all([
      getProjectsByOwner(user.id),
      getOtherProjects(user.id),
      getAllProfiles(),
      getCurrentProfile(user.id),
    ]);
    setMyProjects(mine);
    setOtherProjects(others);
    setAllProfiles(profiles);
    setCurrentProfile(profile);

    const allIds = [...mine, ...others].map(p => p.id);
    if (allIds.length > 0) {
      const summary = await getImportSummaryForProjects(allIds);
      // Rows count only the newest record per (project, schema) so re-pushes
      // don't double-count — the same rule as the MigrationTab grid.
      const latestBySchema = {};
      for (const row of summary) {
        const key = `${row.project_id}|${row.schema_type}`;
        const prev = latestBySchema[key];
        if (!prev || new Date(row.completed_at || 0) > new Date(prev.completed_at || 0)) latestBySchema[key] = row;
      }
      const map = {};
      for (const row of summary) {
        const e = map[row.project_id] || (map[row.project_id] = { schemas: new Set(), rows: 0, last: null });
        e.schemas.add(row.schema_type);
        if (row.completed_at && (!e.last || new Date(row.completed_at) > new Date(e.last))) e.last = row.completed_at;
      }
      for (const row of Object.values(latestBySchema)) {
        map[row.project_id].rows += importRowsCount(row);
      }
      setImportSummaryMap(map);
      setImportSummaryRows(summary);
    } else {
      setImportSummaryMap({});
      setImportSummaryRows([]);
    }
    setLoading(false);
  };

  useEffect(() => { loadProjects(); }, []);

  // Role guard: never strand a non-admin on the admin console (role demoted,
  // or a stale initialAdminTab). Waits for the profile to load before judging.
  useEffect(() => {
    if (section === 'admin' && currentProfile && !isAdmin) setSection('projects');
  }, [section, currentProfile, isAdmin]);

  const handleClaim = async (projectId) => {
    const result = await claimProject(projectId, user.id);
    if (result) { await loadProjects(); setOwnerFilter('me'); }
  };

  const handleConnectAndCreate = async (e) => {
    if (e) e.preventDefault();
    setCreating(true);
    setCreateError('');
    setDuplicateProject(null);
    setNeedsManualName(false);
    setOrgName('');

    setCreateStep('testing');
    const connResult = await testFmxConnection(fmxSiteUrl, fmxApiEmail, fmxApiPassword);
    if (!connResult.success) {
      setCreateError(connResult.message); setCreating(false); setCreateStep(''); return;
    }

    setCreateStep('fetching');
    const { orgName: fetchedOrgName, ...modules } = await fetchFmxModules({ siteUrl: fmxSiteUrl, email: fmxApiEmail, password: fmxApiPassword });
    if (!fetchedOrgName) { setNeedsManualName(true); setCreating(false); setCreateStep(''); return; }
    setOrgName(fetchedOrgName);

    setCreateStep('checking');
    const existing = await getProjectByFmxUrl(fmxSiteUrl.trim());
    if (existing) { setDuplicateProject(existing); setCreating(false); setCreateStep(''); return; }

    setCreateStep('creating');
    const project = await createProject(fetchedOrgName, description.trim() || null, fmxSiteUrl.trim(), user.id);
    if (!project) { setCreateError('Failed to create project. Please try again.'); setCreating(false); setCreateStep(''); return; }

    await saveProjectCredentials({ projectId: project.id, siteUrl: fmxSiteUrl.trim(), email: fmxApiEmail, password: fmxApiPassword, verified: true });
    await updateProjectModules(project.id, modules);

    await loadProjects();
    setMode('idle');
    setFmxSiteUrl(''); setFmxApiEmail(''); setFmxApiPassword(''); setDescription('');
    setCreateStep(''); setCreating(false); setOwnerFilter('me');
  };

  const handleCreateWithManualName = async () => {
    if (!manualName.trim()) return;
    setCreating(true);
    setCreateError('');
    setCreateStep('creating');

    const { orgName: _o, ...modules } = await fetchFmxModules({ siteUrl: fmxSiteUrl, email: fmxApiEmail, password: fmxApiPassword });
    const project = await createProject(manualName.trim(), description.trim() || null, fmxSiteUrl.trim(), user.id);
    if (!project) { setCreateError('Failed to create project.'); setCreating(false); setCreateStep(''); return; }

    await saveProjectCredentials({ projectId: project.id, siteUrl: fmxSiteUrl.trim(), email: fmxApiEmail, password: fmxApiPassword, verified: true });
    await updateProjectModules(project.id, modules);
    await loadProjects();
    setMode('idle');
    setFmxSiteUrl(''); setFmxApiEmail(''); setFmxApiPassword(''); setDescription('');
    setManualName(''); setNeedsManualName(false);
    setCreateStep(''); setCreating(false); setOwnerFilter('me');
  };

  // ---- Derived rows ----
  // allRows: every visible project with computed progress/status (feeds the
  // Dashboard too). rows: the grid view after owner/status/search filters.
  const allRows = useMemo(() => {
    return [...myProjects, ...otherProjects].map(p => {
      const summary = importSummaryMap[p.id] || { schemas: new Set(), rows: 0, last: null };
      const progress = computeProgress(p, summary.schemas);
      const status = projectStatus(progress);
      return { p, progress, status, rowsPushed: summary.rows, last: summary.last || p.updated_at };
    });
  }, [myProjects, otherProjects, importSummaryMap]);

  const rows = useMemo(() => {
    return allRows.filter(r => {
      if (ownerFilter === 'me' && r.p.user_id !== user.id) return false;
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        if (!(`${r.p.name} ${r.p.fmx_site_url || ''}`.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [allRows, ownerFilter, statusFilter, search, user.id]);

  const handleExport = () => {
    const headers = ['Project', 'Site', 'Owner', 'Progress', 'Rows pushed', 'Status', 'Last activity'];
    const out = rows.map(r => ({
      Project: r.p.name,
      Site: r.p.fmx_site_url || '',
      Owner: profileMap[r.p.user_id] || 'Unassigned',
      Progress: `${r.progress.completed}/${r.progress.total}`,
      'Rows pushed': r.rowsPushed,
      Status: STATUS_META[r.status].label,
      'Last activity': daysSince(r.last),
    }));
    downloadCSV('projects.csv', headers, out);
  };

  const sidebarUser = {
    name: currentProfile?.full_name || user?.email || 'User',
    title: isAdmin ? 'Admin' : 'Implementation',
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', fontSize: 14, borderRadius: T.radiusBtn,
    border: `1px solid ${T.borderStrong}`, outline: 'none', boxSizing: 'border-box',
    fontFamily: T.fontSans,
  };

  // Column style helpers (FMX data grid)
  const hs = (w, right) => ({ textAlign: right ? 'right' : 'left', font: `700 12px/16px ${T.fontSans}`, color: T.inkMuted, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '9px 14px', background: T.bgMuted, borderBottom: `1px solid ${T.borderSubtle}`, width: w, whiteSpace: 'nowrap' });
  const cs = { padding: '11px 14px', borderBottom: `1px solid ${T.borderSubtle}`, font: `400 14px/20px ${T.fontSans}`, color: T.ink, verticalAlign: 'middle' };

  // Global-mode sidebar: Projects / Dashboard / Activity. The admin console
  // is reached only via the gear beside the profile; while it's open the
  // gear is the sole highlight (no nav item active).
  const sidebar = (
    <MigSidebar
      current={section}
      adminActive={section === 'admin'}
      onNavigate={setSection}
      onAdmin={() => setSection('admin')}
      user={sidebarUser}
      isAdmin={isAdmin}
    />
  );
  const topBar = (
    <TopBar
      context="FMX Implementation"
      searchValue={search}
      onSearch={setSearch}
      rightSlot={
        <UserMenu
          user={user}
          profile={currentProfile}
          onOpenProfile={() => setShowProfileModal(true)}
          onOpenAdminPanel={() => { setSection('admin'); setAdminTab('users'); }}
          onSignOut={async () => { await supabase.auth.signOut(); }}
        />
      }
    />
  );

  // ---- Content per section ----
  let content;
  if (section === 'projects') {
    const subtitle = `${rows.length} ${rows.length === 1 ? 'migration' : 'migrations'} · FMX Implementation team`;
    content = (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ContentHeader
          crumbs={['Projects']}
          title="Projects"
          subtitle={subtitle}
          actions={<>
            <Button variant="neutral" icon="download" onClick={handleExport}>Export</Button>
            <Button variant="primary" icon="plus" onClick={() => { setMode('create'); setCreateError(''); setDuplicateProject(null); setNeedsManualName(false); setOrgName(''); }}>New project</Button>
          </>}
        />
        <div style={{ padding: '12px 24px', background: T.bg, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="neutral" size="sm" icon="filter" onClick={() => { setStatusFilter('all'); setOwnerFilter('me'); }}>Filters</Button>
          <FilterChip icon="circle-check" label="Status" value={statusFilter === 'all' ? 'All' : STATUS_META[statusFilter].label}
            onClick={() => setStatusFilter(s => s === 'all' ? 'active' : s === 'active' ? 'complete' : s === 'complete' ? 'setup' : 'all')} />
          <FilterChip icon="user" label="Owner" value={ownerFilter === 'me' ? 'Me' : 'Anyone'}
            onClick={() => setOwnerFilter(o => o === 'me' ? 'anyone' : 'me')} />
        </div>
        <div style={{ padding: '0 24px 24px', background: T.bg, flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px 0', color: T.inkSoft, fontSize: 14 }}>Loading projects…</div>
          ) : rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem 1rem' }}>
              <p style={{ font: `700 16px ${T.fontSans}`, color: T.ink, margin: '0 0 6px' }}>No projects to show</p>
              <p style={{ fontSize: 14, color: T.inkMuted, margin: '0 0 18px' }}>
                {ownerFilter === 'me' ? 'Create your first migration project to get started.' : 'No projects match these filters.'}
              </p>
              <Button variant="primary" icon="plus" onClick={() => setMode('create')}>New project</Button>
            </div>
          ) : (
            <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, overflowX: 'auto' }}>
              <table style={{ width: '100%', minWidth: 920, borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={hs()}>Project</th>
                  <th style={hs(160)}>Owner</th>
                  <th style={hs(220)}>Progress</th>
                  <th style={hs(110, true)}>Rows pushed</th>
                  <th style={hs(130)}>Last activity</th>
                  <th style={hs(130)}>Status</th>
                  <th style={hs(110)} />
                </tr></thead>
                <tbody>
                  {rows.map(({ p, progress, status, rowsPushed, last }, i) => {
                    const sb = STATUS_META[status];
                    const pct = progress.total > 0 ? Math.round((progress.completed / progress.total) * 100) : 0;
                    const ownerName = profileMap[p.user_id] || 'Unassigned';
                    const zebra = i % 2 ? '#fcfcfc' : T.white;
                    const isActiveWizard = p.id === activeProjectId && activeWizardSchema;
                    return (
                      <tr key={p.id} style={{ background: zebra, cursor: 'pointer' }}
                        onClick={() => onSelectProject(p)}
                        onMouseEnter={e => e.currentTarget.style.background = T.bgMuted}
                        onMouseLeave={e => e.currentTarget.style.background = zebra}>
                        <td style={cs}>
                          <div style={{ fontWeight: 600, color: T.ink }}>{p.name}</div>
                          <div style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, marginTop: 1 }}>{p.fmx_site_url || '—'}</div>
                          {isActiveWizard && (
                            <div style={{ font: `600 11px ${T.fontSans}`, color: T.accentOrange, marginTop: 3 }}>
                              {activeWizardSchema} in progress
                            </div>
                          )}
                        </td>
                        <td style={{ ...cs, fontSize: 13 }}>
                          <OwnerChip name={ownerName} />
                          {!p.user_id && !isAdmin && (
                            <Button variant="secondary" size="sm" style={{ marginLeft: 8 }} onClick={(e) => { e.stopPropagation(); handleClaim(p.id); }}>Claim</Button>
                          )}
                        </td>
                        <td style={cs}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ flex: 1 }}><Meter pct={pct} h={7} /></div>
                            <span style={{ font: `600 12px ${T.fontData}`, color: T.inkMuted, whiteSpace: 'nowrap' }}>{progress.completed}/{progress.total}</span>
                          </div>
                        </td>
                        <td style={{ ...cs, textAlign: 'right', fontFamily: T.fontData, fontWeight: 600, color: rowsPushed ? T.ink : T.border }}>{rowsPushed ? rowsPushed.toLocaleString() : '—'}</td>
                        <td style={{ ...cs, fontSize: 13, color: T.inkMuted }}>{daysSince(last)}</td>
                        <td style={cs}><Badge color={sb.color} icon={sb.icon}>{sb.label}</Badge></td>
                        <td style={{ ...cs, textAlign: 'right', whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                          {status === 'complete' && <Button variant="tertiary" size="sm" icon="file-powerpoint" onClick={() => onSelectProject(p, 'qbr')}>QBR</Button>}
                          <Button variant="tertiary" size="sm" iconAfter="arrow-right" onClick={() => onSelectProject(p)}>Open</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  } else if (section === 'admin') {
    content = (
      <AdminScreen
        currentUser={user}
        currentProfile={currentProfile}
        allProfiles={allProfiles}
        projects={[...myProjects, ...otherProjects]}
        onProfilesChanged={async () => { await loadProjects(); }}
        fieldOverrides={fieldOverrides}
        onFieldOverridesChanged={onFieldOverridesChanged}
        tab={adminTab}
        onTabChange={setAdminTab}
      />
    );
  } else if (section === 'dashboard') {
    content = (
      <DashboardScreen
        projectRows={allRows}
        importRows={importSummaryRows}
        projectNameById={projectNameById}
        onOpenProject={onSelectProject}
      />
    );
  } else {
    content = (
      <ActivityScreen
        importRows={importSummaryRows}
        projectNameById={projectNameById}
      />
    );
  }

  return (
    <AppShell sidebar={sidebar} topBar={topBar}>
      {content}

      {/* Profile edit modal */}
      {showProfileModal && (
        <ProfileEditModal
          user={user}
          profile={currentProfile}
          onClose={() => setShowProfileModal(false)}
          onProfileUpdated={async () => { await loadProjects(); }}
        />
      )}

      {/* Create project modal */}
      {mode === 'create' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: T.white, borderRadius: 12, boxShadow: T.shadowLg, width: '100%', maxWidth: 480, padding: '28px 32px' }}>
            <h2 style={{ font: `700 18px ${T.fontSans}`, color: T.ink, margin: '0 0 20px' }}>New project</h2>

            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>FMX site URL *</label>
              <input style={inputStyle} value={fmxSiteUrl} onChange={e => { setFmxSiteUrl(e.target.value); setDuplicateProject(null); }} placeholder="yoursite.gofmx.com" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>FMX API email *</label>
              <input style={inputStyle} type="email" value={fmxApiEmail} onChange={e => setFmxApiEmail(e.target.value)} placeholder="admin@example.com" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>FMX API password *</label>
              <input style={inputStyle} type="password" value={fmxApiPassword} onChange={e => setFmxApiPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Description <span style={{ color: T.inkSoft, fontWeight: 400 }}>(optional)</span></label>
              <textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} placeholder="Notes about this migration (team, target go-live, etc.)" style={{ ...inputStyle, resize: 'vertical' }} />
            </div>

            {orgName && !duplicateProject && (
              <div style={{ background: T.tint.success, padding: '10px 14px', borderRadius: T.radiusBtn, marginBottom: 14, fontSize: 13, color: T.greenText }}>
                Organization: <strong>{orgName}</strong>
              </div>
            )}

            {duplicateProject && (
              <div style={{ background: T.tint.warning, padding: '12px 14px', borderRadius: T.radiusBtn, marginBottom: 14, fontSize: 13, color: T.orange500 }}>
                <p style={{ margin: '0 0 8px' }}>A project for this FMX site already exists: <strong>{duplicateProject.name}</strong></p>
                <Button variant="neutral" size="sm" onClick={() => { setMode('idle'); setDuplicateProject(null); onSelectProject(duplicateProject); }}>Open existing project</Button>
              </div>
            )}

            {needsManualName && (
              <div style={{ background: T.tint.warning, padding: '12px 14px', borderRadius: T.radiusBtn, marginBottom: 14, border: `1px solid ${T.badge.orange}` }}>
                <p style={{ margin: '0 0 8px', fontSize: 13, color: T.orange500 }}>Could not detect organization name. Please enter one:</p>
                <input style={inputStyle} value={manualName} onChange={e => setManualName(e.target.value)} placeholder="e.g. Riverside School District" autoFocus />
                <Button variant="primary" size="sm" style={{ marginTop: 8 }} disabled={!manualName.trim() || creating} onClick={handleCreateWithManualName}>Create project</Button>
              </div>
            )}

            {createError && <p style={{ color: T.red500, fontSize: 13, margin: '0 0 14px' }}>{createError}</p>}

            {creating && createStep && (
              <p style={{ fontSize: 12, color: T.inkMuted, margin: '0 0 14px' }}>
                {createStep === 'testing' && 'Testing connection…'}
                {createStep === 'fetching' && 'Fetching organization info…'}
                {createStep === 'checking' && 'Checking for duplicates…'}
                {createStep === 'creating' && 'Creating project…'}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              <Button variant="primary" style={{ flex: 1, height: 40 }} disabled={!fmxSiteUrl || !fmxApiEmail || !fmxApiPassword || creating || !!duplicateProject} onClick={handleConnectAndCreate}>
                {creating ? 'Connecting…' : 'Connect & Create'}
              </Button>
              <Button variant="neutral" style={{ height: 40 }} onClick={() => { setMode('idle'); setCreateError(''); setDuplicateProject(null); setNeedsManualName(false); setOrgName(''); setDescription(''); }}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
